import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import useHasAllScopes from 'messaging-management-lib/user/hooks/useHasAllScopes';
import Raven from 'raven-js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { bulkArchive, bulkArchiveAndUnpublish } from '../api/archive';
import useWorkflows from '../hooks/useWorkflows';
import EmailList from './EmailList';
import SendAlert from './SendAlert';
import WorkflowAlert from './WorkflowAlert';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function ArchiveModal({
  emails,
  onClose,
  listingLibObserver
}) {
  const hasAllScopes = useHasAllScopes();
  const hasPublishScope = hasAllScopes('email-publish');
  const isBlogOrRssOrScheduled = useMemo(() => {
    return emails && emails.length === 1 && (emails[0].isScheduled() || (emails[0].isBlogEmail() || emails[0].isRssEmail()) &&
    // published blog/rss email
    emails[0].isPublished());
  }, [emails]);
  const shouldSetDefaultUnpublishTrue = useMemo(() => {
    return hasPublishScope && isBlogOrRssOrScheduled;
  }, [hasPublishScope, isBlogOrRssOrScheduled]);
  const [unpublishChecked, setUnpublishChecked] = useState(false);
  useEffect(() => {
    setUnpublishChecked(shouldSetDefaultUnpublishTrue);
  }, [shouldSetDefaultUnpublishTrue]);
  const {
    emailsWithWFInfo,
    loading: isLoadingWorkflows
  } = useWorkflows({
    emails
  });
  const shouldRenderSimpleFooterDescription = useMemo(() => {
    return emails.every(email => {
      return !(email.isAutomatedLike() && email.isPublished()) && !(email.isBlogEmail() && email.isPublished()) && !(email.isRssEmail() && email.isPublished()) && !email.isScheduled();
    });
  }, [emails]);
  const areMultipleEmailsSelected = useMemo(() => emails.length > 1, [emails]);
  const emailIds = useMemo(() => emails.map(email => email.id), [emails]);
  const shouldRenderUnpublishCheckbox = useMemo(() => {
    if (emails.some(email => {
      return (email.isAutomated() || email.isABAutomatedEmail() || email.isRssEmail() || email.isBlogEmail()) && email.isPublished() || email.isScheduled();
    })) {
      return true;
    }
    return false;
  }, [emails]);
  const maybeRenderWorkflowAlert = useMemo(() => {
    if (unpublishChecked) {
      return isLoadingWorkflows ? /*#__PURE__*/_jsx(LoadingSpinner, {}) : /*#__PURE__*/_jsx(WorkflowAlert, {
        emailsWithWFInfo: emailsWithWFInfo
      });
    }
    return null;
  }, [emailsWithWFInfo, isLoadingWorkflows, unpublishChecked]);
  const maybeRenderSendAlert = useMemo(() => {
    if (!unpublishChecked && isBlogOrRssOrScheduled) {
      return /*#__PURE__*/_jsx(SendAlert, {});
    }
    return null;
  }, [isBlogOrRssOrScheduled, unpublishChecked]);
  const removeFromFrameworkTable = useCallback(() => {
    if (listingLibObserver) {
      listingLibObserver.delete(emails.map(email => email.crmObjectId));
    }
  }, [emails, listingLibObserver]);
  const onClickArchive = useCallback(() => {
    if (unpublishChecked) {
      bulkArchiveAndUnpublish(emailIds).then(removeFromFrameworkTable).catch(error => {
        var _error$responseJSON$m, _error$responseJSON;
        FloatingAlertStore.addAlert({
          type: 'danger',
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.errors.email.archiveAndUnpublish",
            options: {
              count: emailIds.length
            }
          })
        });
        Raven.captureException(new Error('Error when archiving and unpublishing email'), {
          extra: {
            message: (_error$responseJSON$m = (_error$responseJSON = error.responseJSON) === null || _error$responseJSON === void 0 ? void 0 : _error$responseJSON.message) !== null && _error$responseJSON$m !== void 0 ? _error$responseJSON$m : error
          }
        });
      });
    } else {
      bulkArchive(emailIds).then(removeFromFrameworkTable).catch(error => {
        var _error$responseJSON$m2, _error$responseJSON2;
        FloatingAlertStore.addAlert({
          type: 'danger',
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.errors.email.archive",
            options: {
              count: emailIds.length
            }
          })
        });
        Raven.captureException(new Error('Error when archiving email'), {
          extra: {
            message: (_error$responseJSON$m2 = (_error$responseJSON2 = error.responseJSON) === null || _error$responseJSON2 === void 0 ? void 0 : _error$responseJSON2.message) !== null && _error$responseJSON$m2 !== void 0 ? _error$responseJSON$m2 : error
          }
        });
      });
    }
    onClose();
  }, [emailIds, onClose, removeFromFrameworkTable, unpublishChecked]);
  if (!emails || emails.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Modal, {
    "data-selenium": areMultipleEmailsSelected ? 'archive-multi-modal' : 'archive-modal',
    "data-test-id": "email-archive-modal",
    width: 500,
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "email-management.emailArchive.title",
          options: {
            count: emails.length
          }
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        children: [/*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "email-management.emailArchive.subtitle",
          options: {
            count: emails.length,
            emailName: emails[0].name
          }
        }), shouldRenderUnpublishCheckbox && /*#__PURE__*/_jsx(Tooltip, {
          disabled: hasPublishScope,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.emailArchive.unpublish.tooltip"
          }),
          children: /*#__PURE__*/_jsx(Checkbox, {
            marginBlockStart: "400",
            disabled: !hasPublishScope,
            checked: unpublishChecked,
            onChange: e => setUnpublishChecked(e.target.checked),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "email-management.emailArchive.unpublish.label"
            })
          })
        }), maybeRenderWorkflowAlert, maybeRenderSendAlert, areMultipleEmailsSelected && /*#__PURE__*/_jsx(EmailList, {
          isLoadingWorkflows: isLoadingWorkflows,
          emailsWithWFInfo: emailsWithWFInfo,
          shouldRenderWorkflow: unpublishChecked
        }), /*#__PURE__*/_jsx(View, {
          marginBlockStart: "400",
          children: /*#__PURE__*/_jsx(Text, {
            color: "text-primary-subtle",
            children: shouldRenderSimpleFooterDescription ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: "email-management.emailArchive.description"
            }) : /*#__PURE__*/_jsx(FormattedMessage, {
              message: "email-management.emailArchive.enhancedDescription"
            })
          })
        })]
      })
    }), /*#__PURE__*/_jsx(DialogFooter, {
      children: /*#__PURE__*/_jsxs(ButtonWrapper, {
        children: [/*#__PURE__*/_jsx(Button, {
          "data-selenium": areMultipleEmailsSelected ? 'archive-multi-modal-confirmation' : 'archive-modal-confirmation',
          onClick: onClickArchive,
          use: "primary",
          children: unpublishChecked ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.emailArchive.confirm.archiveAndUnpublish"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.emailArchive.confirm.archive"
          })
        }), /*#__PURE__*/_jsx(Button, {
          onClick: onClose,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.emailArchive.cancel"
          })
        })]
      })
    })]
  });
}